import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Modal, Field } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import type { PartnerResponse, ProgramResponse } from '@/shared/api/types'
import { createPartner, deletePartner, listPartners, updatePartner } from '../api'
import { Money } from '../parts'
import { PARTNER_TYPE_ORDER, PartnerTypeBadge, partnerTypeLabel } from '../vocab'

/**
 * Partners.
 *
 * PollSphere keyed partners by their NAME string, so two organisations called
 * "Hope Foundation" merged into one node on its ecosystem graph and one row in
 * its funding table. These have ids.
 *
 * `contactEmail` is stored in plaintext, unlike every other contact detail in
 * this feature, and that is deliberate: it is an organisation's business
 * address, not a private individual's. A beneficiary's phone gets the
 * encryption; a partnerships desk's inbox does not need it.
 */
export function PartnersPanel({ program }: { program: ProgramResponse }) {
  const canUpdate = useCan('program:update')
  const [rows, setRows] = useState<PartnerResponse[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<PartnerResponse | 'new' | null>(null)

  const load = () => listPartners(program.id).then(setRows).catch((e) => setError(explain(e)))

  useEffect(() => {
    let alive = true
    listPartners(program.id)
      .then((r) => { if (alive) setRows(r) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [program.id])

  const remove = async (p: PartnerResponse) => {
    if (!window.confirm(`Remove ${p.name} from this programme?`)) return
    try { await deletePartner(program.id, p.id); load() }
    catch (e) { setError(explain(e)) }
  }

  if (!rows) return <LoadingBlock label="Loading partners…" />

  return (
    <div className="space-y-3 px-1">
      <ErrorStrip message={error} />
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-erp-sm font-medium">{rows.length} partner{rows.length === 1 ? '' : 's'}</h4>
        {canUpdate && (
          <button type="button" className="erp-btn h-7 text-erp-xs" onClick={() => setEditing('new')}>
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-erp-xs text-muted-foreground">No partners recorded.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((p) => (
            <li key={p.id} className="rounded-md border border-border p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-erp-sm font-medium">{p.name}</span>
                <PartnerTypeBadge code={p.partnerTypeCode} />
                {p.contributionAmount !== null && p.contributionAmount !== undefined && (
                  <span className="text-erp-xs text-muted-foreground">
                    {/* `PartnerResponse` carries no currency. Stamping the
                        programme's on it renders a partner's USD 50,000 as
                        "KES 50,000" — off by a factor of a hundred and thirty. */}
                    contributes <Money value={p.contributionAmount} />
                    <span className="text-muted-foreground"> (currency not recorded)</span>
                  </span>
                )}
                {canUpdate && (
                  <span className="ml-auto flex gap-1">
                    <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                      onClick={() => setEditing(p)}>Edit</button>
                    <button type="button" className="erp-btn-ghost h-7 w-7 p-0"
                      aria-label={`Remove ${p.name}`} onClick={() => remove(p)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                )}
              </div>
              <p className="text-erp-xs text-muted-foreground">
                {p.contactName ?? 'no named contact'}
                {p.contactEmail ? ` · ${p.contactEmail}` : ''}
              </p>
              {p.roleNote && <p className="text-erp-xs mt-1">{p.roleNote}</p>}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <PartnerDialog
          programId={program.id}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}

function PartnerDialog({ programId, existing, onClose, onSaved }: {
  programId: string; existing?: PartnerResponse; onClose: () => void; onSaved: () => void
}) {
  const [name, setName] = useState(existing?.name ?? '')
  const [partnerTypeCode, setType] = useState(existing?.partnerTypeCode ?? 'NGO')
  const [contactName, setContactName] = useState(existing?.contactName ?? '')
  const [contactEmail, setContactEmail] = useState(existing?.contactEmail ?? '')
  const [contributionAmount, setAmount] = useState(
    existing?.contributionAmount == null ? '' : String(existing.contributionAmount))
  const [roleNote, setRoleNote] = useState(existing?.roleNote ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setError(null)
    const body = {
      name, partnerTypeCode,
      contactName: contactName || null, contactEmail: contactEmail || null,
      contributionAmount: contributionAmount === '' ? null : Number(contributionAmount),
      roleNote: roleNote || null,
    }
    try {
      if (existing) await updatePartner(programId, existing.id, body)
      else await createPartner(programId, body)
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title={existing ? `Edit ${existing.name}` : 'Add a partner'} footer={(
      <>
        <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="erp-btn-primary" onClick={submit} disabled={busy || !name}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </>
    )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Field label="Organisation" required>
          <input className="erp-input" value={name} maxLength={200}
            onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select className="erp-input" value={partnerTypeCode}
              onChange={(e) => setType(e.target.value as typeof partnerTypeCode)}>
              {PARTNER_TYPE_ORDER.map((t) => (
                <option key={t} value={t}>{partnerTypeLabel(t)}</option>
              ))}
            </select>
          </Field>
          <Field label="Contribution">
            <input className="erp-input" type="number" value={contributionAmount}
              onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Contact name">
            <input className="erp-input" value={contactName ?? ''} maxLength={160}
              onChange={(e) => setContactName(e.target.value)} />
          </Field>
          <Field label="Contact email" hint="A business address, stored as typed.">
            <input className="erp-input" type="email" value={contactEmail ?? ''} maxLength={255}
              onChange={(e) => setContactEmail(e.target.value)} />
          </Field>
        </div>
        <Field label="Role">
          <input className="erp-input" value={roleNote ?? ''} maxLength={512}
            onChange={(e) => setRoleNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
